import React from "react";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Toolbar,
  Menu,
  Button,
  MenuItem,
  Badge,
  Link,
  Avatar,
  CardMedia,
  Container,
  Typography,
} from "@mui/material";
import LanguageSelect from "../../../../Components/Widgets/LanguageSelect";
import { useTheme } from "@mui/material/styles";

const BRAND = "#f05d32";

const CouponBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
    background: BRAND,
  },
}));

const CouponBadge2 = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -13,
    top: 12,
    padding: "0 4px",
    background: BRAND,
  },
}));

const IconsButton = styled(Button)(() => ({
  color: "#fff",
  fontSize: "25px",
  backgroundColor: "transparent",
  marginRight: 5,
  paddingLeft: 0,
  paddingRight: 0,
  minWidth: 40,
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": { color: BRAND, backgroundColor: "#fff" },
}));

const MyMenuItem = styled(MenuItem)(() => ({
  color: "#7d7d7d",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "transparent",
    color: BRAND,
  },
}));

function LocationChip({
  isTakeaway,
  label,
  onClick,
  compact = false,
  component,
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={isTakeaway ? "Change store" : "Change address"}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 0.75 : 1,
        maxWidth: { xs: 100, sm: 180, md: 260 },
        m: 0,
        px: compact ? 1 : 1.5,
        py: compact ? 0.6 : 0.75,
        border: "1px solid rgba(255,255,255,0.35)",
        borderRadius: "50px",
        bgcolor: "rgba(0,0,0,0.16)",
        color: "#fff",
        cursor: "pointer",
        font: "inherit",
        textAlign: "left",
        transition: "background 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          bgcolor: "rgba(0,0,0,0.28)",
          borderColor: "rgba(255,255,255,0.55)",
        },
      }}
    >
      <Box
        sx={{
          width: compact ? 26 : 30,
          height: compact ? 26 : 30,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: compact ? 12 : 13,
        }}
      >
        <i
          className={
            isTakeaway ? "fa-light fa-store" : "fa-light fa-location-dot"
          }
        />
      </Box>
      <Box sx={{ minWidth: 0, overflow: "hidden" }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            lineHeight: 1.1,
            opacity: 0.8,
            display: compact ? "none" : "block",
          }}
        >
          {isTakeaway ? component.trans("store") : "Delivery"}
        </Typography>
        <Typography
          sx={{
            fontSize: compact ? 12 : 13,
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box
        component="i"
        className="fa-light fa-chevron-down"
        sx={{
          fontSize: 10,
          opacity: 0.75,
          flexShrink: 0,
          display: { xs: "none", md: "inline" },
        }}
      />
    </Box>
  );
}

export default function MainAppbar(props) {
  const { component } = props;
  const theme = useTheme();
  const devHelper = component.getHelpers("dev");
  const coupons = component.getData("default.coupons", []);

  const isAuth = component.isAuthenticated();
  const username = component
    .getAuth()
    .getUserAttribute("name")
    .split(" ")
    .map((word) => word.charAt(0))
    .splice(0, 2)
    .join("");
  const order = component.getData("default.order", null);
  const filesUrl = component.getHelpers("env").get("files_url");
  const img_version = component.getHelpers("value").getValue("images-version");
  const deliveryType = devHelper.getObjectValue(order, "delivery_type");
  const isTakeaway = deliveryType === "takeaway";
  const spotSlug = devHelper.getObjectValue(order, "spot.slug");
  const shippingAddress = devHelper.getObjectValue(
    order,
    "shipping_address.address",
    "",
  );

  const locationLabel = isTakeaway
    ? spotSlug
      ? component.trans(`${spotSlug}-name`)
      : component.ucfirst("takeaway")
    : shippingAddress || component.ucfirst("delivery");

  const openLocationDialog = () => {
    component
      .getComponents()
      .findById(isTakeaway ? "delivery-type-dialog" : "select-address-dialog")
      .forEach((comp) => {
        comp.setData({
          "default.open": true,
        });
        if (!isTakeaway) {
          setTimeout(() => {
            comp.retrieveAddresses();
            comp.initGoogleAutocomplete("shipping_address");
          }, 500);
        }
      });
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Toolbar
      disableGutters
      sx={{
        minHeight: "0px !important",
        backgroundColor: BRAND,
        boxShadow: "0 2px 12px rgba(240, 93, 50, 0.25)",
      }}
    >
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: {
            xs: 1,
            sm: 2,
            md: 3,
            lg: 6,
          },
          height: {
            xs: theme.appbar_height.mobile,
            md: theme.appbar_height.desktop,
          },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: "none",
              display: "block",
              flexShrink: 0,
            }}
          >
            <CardMedia
              component="img"
              image={`${filesUrl}/assets/images/logo.png?v=${img_version}`}
              sx={{
                width: { xs: "108px", sm: "140px", md: "180px" },
              }}
              alt="il Palatino Home"
              title="il Palatino Home"
            />
          </Link>

          {isAuth && deliveryType && (
            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
              <LocationChip
                isTakeaway={isTakeaway}
                label={locationLabel}
                onClick={openLocationDialog}
                component={component}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
            flexShrink: 0,
          }}
        >
          {isAuth && deliveryType && (
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <LocationChip
                isTakeaway={isTakeaway}
                label={locationLabel}
                onClick={openLocationDialog}
                compact
                component={component}
              />
            </Box>
          )}
          <LanguageSelect {...props} />
          {!isAuth ? (
            <Button
              sx={{
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.7)",
                borderRadius: 50,
                fontSize: { xs: "13px", md: "15px" },
                fontWeight: 600,
                px: { xs: 1.5, md: 2.25 },
                py: { xs: "5px", md: "6px" },
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#fff",
                  color: BRAND,
                  borderColor: "#fff",
                },
              }}
              disableRipple
              onClick={() =>
                component
                  .getComponents()
                  .findById("login-register-dialog")
                  .first()
                  .setData({
                    "default.open": true,
                  })
              }
            >
              Order Now
            </Button>
          ) : (
            <>
              <IconsButton
                aria-owns={anchorEl ? "simple-menu" : undefined}
                aria-haspopup="true"
                onClick={(e) => handleClick(e, "account")}
                disableRipple
                sx={{ mr: { xs: "0px", md: "5px" } }}
              >
                <CouponBadge badgeContent={coupons.length} color="primary">
                  <Avatar
                    sx={{
                      width: { xs: "30px", md: "34px" },
                      height: { xs: "30px", md: "34px" },
                      fontSize: { xs: "14px", md: "16px" },
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#000",
                      color: "#fff",
                    }}
                  >
                    <Box>{username}</Box>
                  </Avatar>
                </CouponBadge>
              </IconsButton>
              <IconsButton
                disableRipple
                onClick={() => {
                  component.toggleCartDrawer();
                }}
                sx={{ mr: { xs: "0px", md: "5px" } }}
                aria-label="Cart"
              >
                <StyledBadge
                  badgeContent={devHelper.getObjectValue(
                    order,
                    "amounts.items",
                    0,
                  )}
                  showZero
                  sx={{
                    fontSize: { xs: "20px", md: "22px" },
                    "& .MuiBadge-badge": {
                      visibility:
                        devHelper.getObjectValue(order, "amounts.items", 0) ===
                        0
                          ? "hidden"
                          : "visible",
                    },
                  }}
                >
                  <i className="fa-light fa-bag-shopping"></i>
                </StyledBadge>
              </IconsButton>
            </>
          )}
        </Box>
      </Container>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            mt: "10px",
            borderRadius: "12px",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 58,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <MyMenuItem component={RouterLink} to="/profile" onClick={handleClose}>
          {component.ucfirst("profile-btn")}
        </MyMenuItem>
        <MyMenuItem
          component={RouterLink}
          to="/addresses"
          onClick={handleClose}
        >
          {component.ucfirst("addresses-btn")}
        </MyMenuItem>
        <MyMenuItem component={RouterLink} to="/orders" onClick={handleClose}>
          {component.ucfirst("orders-btn")}
        </MyMenuItem>
        <MyMenuItem component={RouterLink} to="/coupons" onClick={handleClose}>
          <CouponBadge2 badgeContent={coupons.length} color="primary">
            {component.ucfirst("coupons-btn")}
          </CouponBadge2>
        </MyMenuItem>
        <MyMenuItem onClick={() => component.getPage().logout()}>
          {component.ucfirst("logout-btn")}
        </MyMenuItem>
      </Menu>
    </Toolbar>
  );
}
